import React, { useState } from "react";
import { useMutation } from "react-query";
import Modal from "react-bootstrap/Modal";
import Table from "../../Components/Table";
import StatusHandler from "../../Components/Table/StatusHandler";
import PageHeader from "../../Components/PageHeader";
import useFetchData from "../../Hooks/useFetchData";
import useTableControls from "../../Hooks/useTableControls";
import { Success, Confirmation, Error } from "../../Components/Modal";
import {
  getContactInquiries,
  getContactInquiryDetails,
  updateContactInquiryStatus,
  deleteContactInquiry,
  replyContactInquiry
} from "../../Services/ContactInquiry";
import { format_date, getSerialNumber } from "../../Utils/helpers";
import "./ContactInquiries.css";

const STATUS_FILTER_OPTIONS = [
  { value: "new", label: "New" },
  { value: "read", label: "Read" },
  { value: "replied", label: "Replied" }
];

const statusPillClass = (status) => {
  if (status === "new") return "status-pending";
  if (status === "replied") return "status-active";
  return "status-inactive";
};

export default function ContactInquiries() {
  const {
    perPage,
    setPerPage,
    status,
    setStatus,
    search_string,
    setSearchString,
    from,
    setFrom,
    to,
    setTo
  } = useTableControls();

  const { page, setPage, isFetching, isLoading, data, refetch } = useFetchData(
    "contact-inquiries",
    getContactInquiries,
    [perPage, search_string, status, from, to]
  );

  const [detail, setDetail] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [replySubject, setReplySubject] = useState("Re: Your message to MUVRRS");
  const [replyMessage, setReplyMessage] = useState("");

  const list = data?.data?.data?.docs || [];
  const totalPages = data?.data?.data?.totalPages || 0;
  const totalDocs = data?.data?.data?.totalDocs || 0;

  const statusMutation = useMutation(
    ({ id, nextStatus }) => updateContactInquiryStatus(id, nextStatus),
    {
      onSuccess: (res) => {
        refetch();
        if (res?.data?.data) setDetail(res.data.data);
        Success("Updated", "Status updated successfully.");
      },
      onError: (err) => {
        Error(err?.response?.data?.message || "Failed to update status");
      }
    }
  );

  const deleteMutation = useMutation((id) => deleteContactInquiry(id), {
    onSuccess: () => {
      refetch();
      Success("Deleted", "Inquiry removed.");
      setShowDetail(false);
      setDetail(null);
    },
    onError: (err) => {
      Error(err?.response?.data?.message || "Failed to delete");
    }
  });

  const replyMutation = useMutation(
    ({ id, message, subject }) => replyContactInquiry(id, message, subject),
    {
      onSuccess: (res) => {
        refetch();
        if (res?.data?.data) setDetail(res.data.data);
        setReplyMessage("");
        Success("Reply Sent", "Email sent to the client successfully.");
      },
      onError: (err) => {
        Error(
          err?.response?.data?.message ||
            "Failed to send reply email. Check SMTP settings."
        );
      }
    }
  );

  const openDetail = async (id) => {
    try {
      const res = await getContactInquiryDetails(id);
      const item = res?.data?.data || null;
      setDetail(item);
      setReplySubject("Re: Your message to MUVRRS");
      setReplyMessage("");
      setShowDetail(true);
      refetch();
    } catch (err) {
      Error(err?.response?.data?.message || "Failed to load details");
    }
  };

  const handleSendReply = (e) => {
    e.preventDefault();
    if (!detail?._id) return;
    if (!replyMessage.trim()) {
      Error("Please write a reply message");
      return;
    }
    replyMutation.mutate({
      id: detail._id,
      message: replyMessage.trim(),
      subject: replySubject.trim()
    });
  };

  const handleDelete = (id) => {
    Confirmation("Delete this contact message?", "Delete", () =>
      deleteMutation.mutate(id)
    );
  };

  return (
    <div className="app-content content">
      <div className="content-wrapper">
        <div className="content-body">
          <section id="configuration">
            <div className="row">
              <div className="col-12">
                <div className="user-details-wrapper">
                  <PageHeader
                    title="Contact Inquiries"
                    subtitle="Messages from the landing page Contact Us form — view, reply by email, and track status"
                  />

                  <Table
                    key="contact-inquiries-table"
                    tableTitles={[
                      "S.No.",
                      "Name",
                      "Email",
                      "Phone",
                      "Message",
                      "Status",
                      "Date",
                      "Action"
                    ]}
                    dateFilter
                    showEntries
                    sortBy
                    status_options={STATUS_FILTER_OPTIONS}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    data={list}
                    totalPages={Math.ceil(totalPages || 0)}
                    totalDocs={totalDocs}
                    page={page}
                    setPage={setPage}
                    setSearchString={setSearchString}
                    status={status}
                    setStatus={setStatus}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    from={from}
                    setFrom={setFrom}
                    to={to}
                    setTo={setTo}
                  >
                    {list.map((item, index) => (
                      <tr key={item._id || index}>
                        <td>{getSerialNumber(data?.data?.data, index)}</td>
                        <td className="fw-bold">{item.name || "—"}</td>
                        <td>{item.email || "—"}</td>
                        <td>{item.phone || "—"}</td>
                        <td>
                          <p className="ci-message-preview" title={item.message}>
                            {item.message || "—"}
                          </p>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${statusPillClass(item.status)}`}
                          >
                            {item.status || "—"}
                          </span>
                        </td>
                        <td>
                          {item.createdAt
                            ? format_date(item.createdAt, "MM/DD/YYYY")
                            : "—"}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <button
                              type="button"
                              className="ci-action-btn"
                              onClick={() => openDetail(item._id)}
                              title="View / Reply"
                            >
                              <i className="fas fa-pen" />
                              View
                            </button>
                            <StatusHandler
                              key={`${item._id}-actions`}
                              item={item}
                              onDelete={() => handleDelete(item._id)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </Table>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Modal
        show={showDetail}
        onHide={() => setShowDetail(false)}
        centered
        size="lg"
        scrollable
        className="ci-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Contact Inquiry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail && (
            <>
              <div className="ci-detail-grid">
                <div>
                  <label>Name</label>
                  <div className="ci-detail-value">{detail.name}</div>
                </div>
                <div>
                  <label>Status</label>
                  <div>
                    <span
                      className={`status-pill ${statusPillClass(detail.status)}`}
                    >
                      {detail.status || "—"}
                    </span>
                  </div>
                </div>
                <div>
                  <label>Email</label>
                  <div className="ci-detail-value">
                    <a href={`mailto:${detail.email}`}>{detail.email}</a>
                  </div>
                </div>
                <div>
                  <label>Phone</label>
                  <div className="ci-detail-value">{detail.phone || "—"}</div>
                </div>
                <div className="ci-detail-full">
                  <label>Client Message</label>
                  <div className="ci-detail-message">{detail.message}</div>
                </div>
                <div className="ci-detail-full">
                  <label>Submitted</label>
                  <div className="ci-detail-value">
                    {detail.createdAt
                      ? format_date(detail.createdAt, "MM/DD/YYYY hh:mm A")
                      : "—"}
                  </div>
                </div>
                {detail.adminReply ? (
                  <div className="ci-detail-full">
                    <label>Last Reply Sent</label>
                    <div className="ci-detail-message ci-detail-message--reply">
                      {detail.adminReply}
                      {detail.repliedAt ? (
                        <div className="ci-reply-meta">
                          Sent{" "}
                          {format_date(detail.repliedAt, "MM/DD/YYYY hh:mm A")}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>

              <form className="ci-reply-box" onSubmit={handleSendReply}>
                <h6>Reply to client via email</h6>
                <p className="ci-reply-hint">
                  Sends directly to <strong>{detail.email}</strong>
                </p>
                <label className="form-label">Subject</label>
                <input
                  type="text"
                  className="form-control"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  placeholder="Email subject"
                />
                <label className="form-label mt-2">Reply Message *</label>
                <textarea
                  className="form-control"
                  rows={5}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Write your reply to the client…"
                  required
                />
                <button
                  type="submit"
                  className="siteBtn mt-3"
                  disabled={replyMutation.isLoading}
                >
                  <i
                    className={`fas ${
                      replyMutation.isLoading
                        ? "fa-spinner fa-spin"
                        : "fa-paper-plane"
                    } me-2`}
                  />
                  {replyMutation.isLoading ? "Sending…" : "Send Email Reply"}
                </button>
              </form>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="ci-modal-footer">
          <div className="d-flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={statusMutation.isLoading || !detail?._id}
              onClick={() =>
                statusMutation.mutate({ id: detail._id, nextStatus: "read" })
              }
            >
              Mark Read
            </button>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={() => detail?._id && handleDelete(detail._id)}
            >
              Delete
            </button>
          </div>
          <button
            type="button"
            className="btn btn-light btn-sm"
            onClick={() => setShowDetail(false)}
          >
            Close
          </button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .fw-800 { font-weight: 800; }
        @media (max-width: 576px) {
          .w-100-mobile { width: 100% !important; text-align: center; justify-content: center; }
          .user-details-wrapper { padding: 0.5rem; }
        }
      `}</style>
    </div>
  );
}
